import type { TicMeta } from 'types/tic';

// 'client' is reserved for PR2 — it needs a client join the client-side cache can't do yet
// (tasks carry no timely_client_id and the project's TIC meta carries no client URI), so it is
// not selectable in the UI. It lives in the type so the scope model is forward-compatible.
export type TicLinkedWorkMode = 'all' | 'integration' | 'client' | 'project';

export interface TicLinkedWorkTaskQueryScope {
  toolId?: string;
  projectUri?: string;
  /**
   * Completion state, narrowed by the server BEFORE pagination. Omitted means both; `none` means
   * every status is de-selected and nothing can match.
   */
  status?: 'open' | 'done' | 'none';
  assignedToMe?: true;
}

export interface TicLinkedWorkTaskScope {
  linkedWorkMode: TicLinkedWorkMode;
  assignedToMe: boolean;
  selectedProjectToolId: string | null;
  selectedProjectUri: string | null;
  selectedProjectId: number | null;
  sameIntegrationIsApplied: boolean;
  sameProjectIsApplied: boolean;
  isProjectSelected: boolean;
  taskQueryScope: TicLinkedWorkTaskQueryScope;
  /**
   * Every status toggle is off, so no task can match. Surfaced for the UI's benefit only — the
   * QUERY carries it as `status: 'none'`, so correctness does not depend on a caller reading this.
   */
  statusAdmitsNothing: boolean;
}

interface ResolveLinkedWorkScopeArgs {
  linkedWorkMode: TicLinkedWorkMode;
  assignedToMe: boolean;
  /** Whether an entry/row has a project selected at all (independent of whether it's TIC-synced). */
  isProjectSelected: boolean;
  /** The selected project's TIC metadata, when the project is synced from an integration. */
  projectTic: TicMeta | null | undefined;
  /** The selected project's Timely id, used for the timely_project_id match branch. */
  selectedProjectId: number | null;
  /** The Open toggle. */
  showActive: boolean;
  /** The Done toggle. */
  showCompleted: boolean;
}

// Pure resolution of the linked-work scope. Shared by the manual-logging selector
// (`getTicLinkedWorkTaskScope`, keyed off the hour draft) and AutoSheet's per-row picker
// (`useLinkedWorkOptions`, keyed off the row's project) so both views derive scope identically.
// The caller resolves `linkedWorkMode` (incl. the legacy fallback) and supplies the project
// context as primitives, keeping this layer free of Redux/draft coupling.
export const resolveLinkedWorkScope = ({
  linkedWorkMode,
  assignedToMe,
  isProjectSelected,
  projectTic,
  selectedProjectId,
  showActive,
  showCompleted,
}: ResolveLinkedWorkScopeArgs): TicLinkedWorkTaskScope => {
  const selectedProjectToolId = projectTic?.tool_id ?? null;
  const selectedProjectUri = projectTic?.uri ?? null;
  const sameIntegrationIsApplied =
    linkedWorkMode === 'integration' && selectedProjectToolId != null;
  const sameProjectIsApplied = linkedWorkMode === 'project' && selectedProjectUri != null;

  return {
    linkedWorkMode,
    assignedToMe,
    selectedProjectToolId,
    selectedProjectUri,
    selectedProjectId,
    sameIntegrationIsApplied,
    sameProjectIsApplied,
    isProjectSelected,
    taskQueryScope: {
      ...(sameIntegrationIsApplied && selectedProjectToolId != null
        ? { toolId: selectedProjectToolId }
        : {}),
      // TT-5457: same-project now narrows server-side. getTasks accepts `projectUri` and filters by
      // the task's resolved ancestor project, so the picker pages through the project's FULL task
      // set (including deep subtasks) instead of relying on a partial, recency-ordered page window.
      ...(sameProjectIsApplied && selectedProjectUri != null
        ? { projectUri: selectedProjectUri }
        : {}),
      // TT-5457: status narrows server-side, before pagination. One toggle on is a predicate; both
      // on is no predicate; both OFF is `none` — carried in the scope rather than left to each
      // caller to skip, because four call sites read this scope and only one of them remembered to.
      ...(showActive !== showCompleted
        ? { status: showActive ? ('open' as const) : ('done' as const) }
        : !showActive && !showCompleted
          ? { status: 'none' as const }
          : {}),
      // TT-4408 PR2: re-enable once the backend accepts an assignedToMe query param.
      // ...(assignedToMe ? { assignedToMe: true as const } : {}),
    },
    statusAdmitsNothing: !showActive && !showCompleted,
  };
};

// NOTE (TT-5457): ALL narrowing — scope (all / same-integration / same-project) and status
// (Open / Done) — is applied SERVER-SIDE by `getTasks`, so there is neither a `filterTasksByScope`
// nor a `filterTasksByStatus` here any more. The client can't reproduce the server's ancestor-project resolution, and re-filtering
// client-side dropped deep subtasks the server correctly returned. The pickers browse straight from
// the scope-narrowed, paginated query (see `useScopedTaskBrowser`).

export interface StatusFilterChip {
  /** True when both toggles are on (the default) — no status chip should be shown. */
  isStatusFilterDefault: boolean;
  /** The chip label for the active status filter, or null when at the default. */
  statusChipLabel: string | null;
}

// Maps the Open/Done toggle pair to the active-filter chip both pickers render, so the chip-label
// mapping lives in one place.
export const deriveStatusFilterChip = ({
  showActive,
  showCompleted,
}: {
  showActive: boolean;
  showCompleted: boolean;
}): StatusFilterChip => ({
  isStatusFilterDefault: showActive && showCompleted,
  statusChipLabel:
    !showActive && !showCompleted
      ? 'Status: None'
      : showActive && !showCompleted
        ? 'Status: Open'
        : !showActive && showCompleted
          ? 'Status: Done'
          : null,
});
